import React from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import AddIcon from '@mui/icons-material/Add';

import { useGetRestrictionsPermissions } from '../../../../redux/api/users/usersApi';
import {
  AvatarSize,
  AvatarTypes,
  NewItemId,
} from '../../../../shared/constants';
import { IExercise } from '../../../../types/interfaces/exercise';
import AppExerciseAvatar from '../../app-entity-avatar/app-exercise-avatar/AppExerciseAvatar';
import AppSelectWrapperVirtualized from '../app-select-wrapper/AppSelectWrapperVirtualized';
import { useStyles } from './AppSelectExercise.styles';

const EXERCISE_SELECT_OPTION_ROW_HEIGHT = 48;
const EXERCISE_SELECT_VISIBLE_ROWS_CAP = 14;

function isExerciseSelectValueEmpty(
  exercise: IExercise | '' | null | undefined,
): boolean {
  return (
    exercise == null ||
    exercise === '' ||
    (typeof exercise === 'object' && !exercise.id && !exercise.title)
  );
}

interface IAppSelectExercise {
  exercises: IExercise[];
  onAddButtonClick?: () => void;
  value?: IExercise | null;
  name?: string;
  label?: React.ReactNode;
  placeholder?: string;
  disableClear?: boolean;
  disabled?: boolean;
  onChange: (exercise?: IExercise) => void;
  error?: boolean;
  helperText?: string;
  dataOnboarding?: string;
}

export default function AppSelectExercise(props: IAppSelectExercise) {
  const {
    exercises,
    value,
    onAddButtonClick,
    onChange,
    name,
    label,
    placeholder,
    disableClear,
    disabled,
    error,
    helperText,
    dataOnboarding,
  } = props;
  const { t } = useTranslation();
  const theme = useTheme();
  const { classes } = useStyles(theme);
  const { data: restrictionsPermissions } = useGetRestrictionsPermissions();
  const placeholderText = placeholder ?? t('exercises.selectExercise');

  function renderItem(exercise: IExercise) {
    return (
      <Box display="flex" gap={1} alignItems="center">
        {exercise?.id === NewItemId ? (
          <AddIcon
            style={{
              width: `${AvatarSize.s}px`,
              height: `${AvatarSize.s}px`,
            }}
          />
        ) : (
          <AppExerciseAvatar
            exercise={exercise}
            size={AvatarSize.s}
            type={AvatarTypes.round}
          />
        )}
        <Typography variant="text-md">{exercise?.title}</Typography>
      </Box>
    );
  }

  return (
    <AppSelectWrapperVirtualized
      data-onboarding={dataOnboarding}
      label={label ?? t('common.exercise')}
      className={classes.selectorWithContent}
      fieldName={'title'}
      options={exercises}
      disableClear={disableClear}
      disabled={disabled}
      additionalOptions={
        onAddButtonClick && restrictionsPermissions?.canCreateExercise
          ? [{ id: NewItemId, title: t('exercises.addExercise') }]
          : undefined
      }
      onChange={(e) => {
        if (onAddButtonClick && e?.target?.value?.id === NewItemId) {
          onAddButtonClick();
        } else {
          onChange(e?.target?.value);
        }
      }}
      name={name}
      defaultValue={value}
      renderOption={renderItem}
      renderValue={(selected) =>
        isExerciseSelectValueEmpty(selected) ? (
          <Typography variant="text-md" color="text.secondary">
            {placeholderText}
          </Typography>
        ) : (
          renderItem(selected as IExercise)
        )
      }
      error={error}
      helperText={helperText}
      optionRowHeight={EXERCISE_SELECT_OPTION_ROW_HEIGHT}
      visibleRowsCap={EXERCISE_SELECT_VISIBLE_ROWS_CAP}
    />
  );
}
